<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col-lg-12">
        <form @submit.prevent="saveRoom()">
          <!-- Header Card with Actions -->
          <div class="card">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <h4 class="card-title mb-1">{{ room.id ? 'Editar Sala' : 'Nova Sala' }}</h4>
                  <p class="text-muted mb-0">Preencha as informações da sala</p>
                </div>
                <div>
                  <button type="button" @click="cancelar()" class="btn btn-outline-secondary mr-2" :disabled="saving">
                    <i class="bx bx-x mr-1"></i> Cancelar
                  </button>
                  <button type="submit" class="btn btn-primary" :disabled="saving">
                    <i class="bx bx-save mr-1"></i> {{ saving ? 'Salvando...' : 'Salvar' }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Room Info Section -->
          <div class="card">
            <div class="card-body">
              <h5 class="card-title mb-4">
                <i class="bx bx-door-open text-primary mr-2"></i>
                Informações da Sala
              </h5>
              <div class="row">
                <!-- Thumbnail Section -->
                <div class="col-md-3 text-center mb-4 mb-md-0">
                  <input
                    ref="thumb"
                    type="file"
                    name="thumb"
                    accept="image/*"
                    @change="setImage"
                  />
                  <div class="thumb-upload-wrapper" @click="showChooser">
                    <div class="thumb-preview" v-if="!cropImg && !room.thumb_image">
                      <div class="thumb-placeholder">
                        <i class="bx bxs-cloud-upload"></i>
                        <span>Selecionar imagem</span>
                      </div>
                    </div>
                    <div class="thumb-preview has-image" v-else>
                      <img :src="cropImg || room.thumb_image" class="img-fluid rounded">
                      <div class="thumb-overlay">
                        <i class="bx bx-camera"></i>
                      </div>
                    </div>
                    <p class="text-muted mt-2 mb-0 small">Clique para alterar</p>
                  </div>
                </div>

                <!-- Main Fields -->
                <div class="col-md-9">
                  <div class="row">
                    <div class="col-md-4">
                      <div class="form-group">
                        <label>Tipo da Sala</label>
                        <select
                          class="form-control"
                          v-model="room.type_room"
                        >
                          <option value="">Selecione...</option>
                          <option value="Plenária">Plenária</option>
                          <option value="Ação">Ação</option>
                          <option value="Painel">Painel</option>
                          <option value="Palestra">Palestra</option>
                          <option value="Reunião">Reunião</option>
                          <option value="Bate Papo">Bate Papo</option>
                        </select>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-group">
                        <label>
                          Tipo de Acesso
                          <span class="text-danger">*</span>
                        </label>
                        <select
                          class="form-control"
                          v-model="room.allow_users"
                          :class="{ 'is-invalid': submitted && $v.room.allow_users.$error }"
                        >
                          <option value="0">Todos usuários</option>
                          <option value="1">Restrita</option>
                        </select>
                        <div v-if="submitted && !$v.room.allow_users.required" class="invalid-feedback">
                          O campo tipo é obrigatório
                        </div>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-group">
                        <label>
                          Abertura
                          <span class="text-danger">*</span>
                        </label>
                        <date-picker
                          v-model="room.start_date"
                          type="datetime"
                          :first-day-of-week="1"
                          lang="pt-br"
                          format="DD/MM/YYYY HH:mm"
                          :class="{ 'is-invalid': submitted && $v.room.start_date.$error }"
                        ></date-picker>
                        <div v-if="submitted && !$v.room.start_date.required" class="invalid-feedback">
                          O campo Abertura é obrigatório
                        </div>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-group">
                        <label>
                          Encerramento
                          <span class="text-danger">*</span>
                        </label>
                        <date-picker
                          v-model="room.end_date"
                          type="datetime"
                          :first-day-of-week="1"
                          lang="pt-br"
                          format="DD/MM/YYYY HH:mm"
                          :class="{ 'is-invalid': submitted && $v.room.end_date.$error }"
                        ></date-picker>
                        <div v-if="submitted && !$v.room.end_date.required" class="invalid-feedback">
                          O campo Encerramento é obrigatório
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Translatable Content Section -->
          <div class="card">
            <div class="card-body">
              <h5 class="card-title mb-4">
                <i class="bx bx-world text-primary mr-2"></i>
                Conteúdo
                <span v-if="isMultiLanguage" class="badge badge-soft-info ml-2">Multi-idioma</span>
              </h5>

              <!-- Single Language Mode -->
              <div v-if="!isMultiLanguage">
                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group">
                      <label>
                        Nome da Sala
                        <span class="text-danger">*</span>
                      </label>
                      <input
                        v-model="room.name"
                        type="text"
                        class="form-control"
                        placeholder="Nome da sala"
                        :class="{ 'is-invalid': submitted && $v.room.name.$error }"
                      />
                      <div v-if="submitted && !$v.room.name.required" class="invalid-feedback">
                        O campo nome é obrigatório
                      </div>
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12">
                    <div class="form-group">
                      <label>
                        Descrição
                        <span class="text-danger">*</span>
                      </label>
                      <ckeditor
                        :editor="editor"
                        v-model="room.description"
                        :config="editorConfig"
                        :class="{ 'is-invalid': submitted && $v.room.description.$error }"
                      ></ckeditor>
                      <div v-if="submitted && !$v.room.description.required" class="invalid-feedback d-block">
                        O campo descrição é obrigatório
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Multi Language Mode -->
              <div v-else>
                <b-tabs v-model="selectedLanguageTab" content-class="mt-3" nav-class="nav-tabs-custom">
                  <b-tab
                    v-for="(lang, index) in activeLanguages"
                    :key="lang.id"
                    :active="index === 0"
                  >
                    <template #title>
                      <span class="d-flex align-items-center">
                        <span v-if="lang.flag" class="mr-1">{{ lang.flag }}</span>
                        {{ lang.name || lang.native_name }}
                        <span v-if="lang.is_default" class="badge badge-soft-primary ml-1">Padrão</span>
                      </span>
                    </template>

                    <div class="row">
                      <div class="col-md-6">
                        <div class="form-group">
                          <label>
                            Nome da Sala
                            <span v-if="lang.is_default" class="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            class="form-control"
                            :placeholder="`Nome em ${lang.native_name}`"
                            :value="getTranslationValue(lang.id, 'name')"
                            @input="setTranslationValue(lang.id, 'name', $event.target.value)"
                            :class="{ 'is-invalid': submitted && lang.is_default && $v.room.name.$error }"
                          />
                          <div v-if="submitted && lang.is_default && !$v.room.name.required" class="invalid-feedback">
                            O campo nome é obrigatório
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-12">
                        <div class="form-group">
                          <label>
                            Descrição
                            <span v-if="lang.is_default" class="text-danger">*</span>
                          </label>
                          <ckeditor
                            :editor="editor"
                            :value="getTranslationValue(lang.id, 'description')"
                            @input="setTranslationValue(lang.id, 'description', $event)"
                            :config="editorConfig"
                            :class="{ 'is-invalid': submitted && lang.is_default && $v.room.description.$error }"
                          ></ckeditor>
                          <div v-if="submitted && lang.is_default && !$v.room.description.required" class="invalid-feedback d-block">
                            O campo descrição é obrigatório
                          </div>
                        </div>
                      </div>
                    </div>
                  </b-tab>
                </b-tabs>
              </div>
            </div>
          </div>

          <!-- Mobile Save Button -->
          <div class="card d-md-none">
            <div class="card-body">
              <div class="d-flex justify-content-end">
                <button type="button" @click="cancelar()" class="btn btn-outline-secondary mr-2" :disabled="saving">
                  Cancelar
                </button>
                <button type="submit" class="btn btn-primary" :disabled="saving">
                  {{ saving ? 'Salvando...' : 'Salvar' }}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </Layout>
</template>

<script>
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/locale/pt-br'
import Layout from '@/components/layout/vertical'
import PageHeader from '@/components/page-header'
import appConfig from '@/app.config'
import CKEditor from '@ckeditor/ckeditor5-vue'
import FullEditor from 'ckeditor5-build-full'
import { errorMessage, successMessage } from '@/components/alertsMessages'
import { mapActions, mapState } from 'vuex'
import { required } from 'vuelidate/lib/validators'
import translatableMixin from '@/mixins/translatableMixin'

export default {
  components: {
    DatePicker,
    Layout,
    PageHeader,
    ckeditor: CKEditor.component
  },
  mixins: [translatableMixin],
  name: 'Sala',
  page: {
    title: 'Sala',
    meta: [{
      name: 'description',
      content: appConfig.description
    }]
  },
  data () {
    return {
      editor: FullEditor,
      editorConfig: {},
      room: {
        id: '',
        name: '',
        type_room: '',
        title: '',
        description: '',
        thumb_image: '',
        allow_users: 0
      },
      thumb: require('@/assets/images/image-default.png'),
      imgSrc: '',
      cropImg: '',
      submitted: false,
      saving: false,
      selectedLanguageTab: 0,
      title: 'Sala',
      items: [{
        text: 'Salas',
        href: '/rooms'
      },
      {
        text: 'Nova Sala',
        active: true
      }
      ]
    }
  },
  validations: {
    room: {
      name: { required },
      allow_users: { required },
      description: { required },
      start_date: { required },
      end_date: { required }
    }
  },
  async mounted () {
    await this.loadTranslationSettings()
    this.loadItem()
  },
  computed: {
    ...mapState('room', { data: 'room' })
  },
  methods: {
    ...mapActions('room', {
      fetch: 'GetRoom',
      postData: 'SaveRoom'
    }),
    cancelar () {
      this.$router.push({ name: 'rooms' })
    },
    async loadItem () {
      if (this.$route.params.id !== undefined) {
        let loader = this.$loading.show()
        this.items[1].text = 'Editar Sala'
        await this.fetch(this.$route.params.id)
        this.room = this.data
        this.room.start_date = this.$moment(this.room.start_date, 'YYYY-MM-DD HH:mm').toDate()
        this.room.end_date = this.$moment(this.room.end_date, 'YYYY-MM-DD HH:mm').toDate()
        // Load translations for this room
        if (this.isMultiLanguage) {
          await this.loadTranslations('room', this.$route.params.id)
        }
        loader.hide()
        return
      }
      // Initialize translations for new room
      if (this.isMultiLanguage) {
        this.initializeTranslations(['name', 'description'])
      }
    },
    setImage (e) {
      const file = e.target.files[0]
      if (file.type.indexOf('image/') === -1) {
        errorMessage('Alerta: Por favor selecione uma imagem.')
        return
      }
      const isLt2M = file.size / 1024 / 1024 < 2
      if (!isLt2M) {
        errorMessage('Alerta: Tamanho máximo 2MB!')
        return
      }
      if (typeof FileReader === 'function') {
        const reader = new FileReader()
        reader.onload = (event) => {
          this.cropImg = event.target.result
        }
        reader.readAsDataURL(file)
        this.imgSrc = file
      } else {
        errorMessage('ERROR: Browser não suporta FileReader')
      }
    },
    showChooser () {
      this.$refs.thumb.click()
    },
    getTranslationValue (languageId, field) {
      if (languageId === this.defaultLanguageId) {
        if (this.translationData[languageId] && this.translationData[languageId][field]) {
          return this.translationData[languageId][field]
        }
        return this.room[field] || ''
      }
      if (this.translationData[languageId] && this.translationData[languageId][field]) {
        return this.translationData[languageId][field]
      }
      return ''
    },
    setTranslationValue (languageId, field, value) {
      if (!this.translationData[languageId]) {
        this.$set(this.translationData, languageId, {})
      }
      this.$set(this.translationData[languageId], field, value)

      if (languageId === this.defaultLanguageId) {
        this.room[field] = value
      }
    },
    async saveRoom () {
      if (this.saving) {
        return
      }

      this.submitted = true
      this.$v.room.$touch()
      if (this.$v.room.$invalid) {
        window.scroll(0, 0)
        return
      }

      this.saving = true
      let loader = this.$loading.show()
      const formData = new FormData()
      formData.append('id', this.room.id)
      formData.append('name', this.room.name)
      formData.append('type_room', this.room.type_room || '')
      formData.append('allow_users', this.room.allow_users)
      formData.append('description', this.room.description)
      formData.append('start_date', this.$moment(this.room.start_date).format('YYYY-MM-DD HH:mm:ss'))
      formData.append('end_date', this.$moment(this.room.end_date).format('YYYY-MM-DD HH:mm:ss'))
      if (this.imgSrc) {
        let ext = this.imgSrc.type.split('/')[1]
        formData.append('thumb_image', this.imgSrc, 'thumbnail.' + ext)
      }
      try {
        const res = await this.postData(formData)
        let roomId = this.room.id
        if (res && res.data && res.data.data && res.data.data.id) {
          roomId = res.data.data.id
        } else if (res && res.data && res.data.id) {
          roomId = res.data.id
        }
        if (this.isMultiLanguage && roomId) {
          await this.saveTranslations('room', roomId)
        }
        let message = 'Sucesso: Dados atualizados com sucesso.'
        let delay = 2500
        loader.hide()
        successMessage(message, delay)
        this.cancelar()
      } catch (err) {
        console.error('Error saving room:', err)
        errorMessage('Alerta: ' + err.message)
        loader.hide()
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
input[type="file"] {
  display: none;
}

/* Thumbnail Upload Styles */
.thumb-upload-wrapper {
  cursor: pointer;
}

.thumb-preview {
  position: relative;
  width: 100%;
  max-width: 200px;
  min-height: 150px;
  margin: 0 auto;
  border-radius: 0.5rem;
  overflow: hidden;
  border: 2px dashed #e9ecef;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
}

.thumb-preview:hover {
  border-color: #556ee6;
}

.thumb-preview.has-image {
  border-style: solid;
  border-color: #e9ecef;
}

.thumb-preview.has-image:hover {
  border-color: #556ee6;
}

.thumb-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #6c757d;
}

.thumb-placeholder i {
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
}

.thumb-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumb-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.thumb-preview.has-image:hover .thumb-overlay {
  opacity: 1;
}

.thumb-overlay i {
  font-size: 2rem;
  color: #fff;
}

/* Card Styles */
.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: none;
  margin-bottom: 1.5rem;
}

.card-title {
  display: flex;
  align-items: center;
  margin-bottom: 0;
}

.card-title i {
  font-size: 1.25rem;
}

/* Badge Styles */
.badge-soft-info {
  background-color: rgba(85, 110, 230, 0.1);
  color: #556ee6;
}

.badge-soft-primary {
  background-color: rgba(85, 110, 230, 0.18);
  color: #556ee6;
  font-size: 0.65rem;
  padding: 0.15rem 0.4rem;
}

/* Date Picker Validation */
.mx-datepicker.is-invalid {
  border: 1px solid #f46a6a !important;
  border-radius: 4px !important;
}

/* CKEditor Styles */
.ck-editor__editable {
  min-height: 200px;
}

.ck.ck-editor {
  width: 100%;
}

.ck.ck-editor.is-invalid .ck-editor__editable {
  border-color: #f46a6a !important;
}
</style>
